/**
 * Shared normalization utilities
 * 
 * Consolidates duplicate normalizeString, normalizeNumber, and related functions
 * from across the codebase into a single source of truth.
 */

/**
 * Normalize a value to a string, trimming whitespace
 * Returns undefined if value is not a string or is empty after trimming
 * 
 * @param value - Value to normalize
 * @returns Normalized string or undefined
 * 
 * @example
 * normalizeString("  hello  ") // "hello"
 * normalizeString(123) // undefined
 * normalizeString("") // undefined
 * normalizeString(null) // undefined
 */
export function normalizeString(value: unknown): string | undefined {
  if (typeof value !== 'string') {
    return undefined;
  }

  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

/**
 * Normalize a value to a string with a fallback
 * Returns the fallback if value is not a string
 * 
 * @param value - Value to normalize
 * @param fallback - Fallback value (default: '')
 * @returns Normalized string or fallback
 * 
 * @example
 * normalizeStringWithFallback("hello") // "hello"
 * normalizeStringWithFallback(123, "default") // "default"
 * normalizeStringWithFallback(null, "") // ""
 */
export function normalizeStringWithFallback(value: unknown, fallback = ''): string {
  return typeof value === 'string' ? value : fallback;
}

/**
 * Normalize a value to an optional string (string | null)
 * Returns null if value is not a string or is empty
 * 
 * @param value - Value to normalize
 * @returns Normalized string or null
 * 
 * @example
 * normalizeOptionalString("hello") // "hello"
 * normalizeOptionalString("") // null
 * normalizeOptionalString(null) // null
 */
export function normalizeOptionalString(value: unknown): string | null {
  if (typeof value !== 'string') {
    return null;
  }

  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

/**
 * Normalize a value to a number
 * Returns 0 if value cannot be converted to a valid number
 * 
 * @param value - Value to normalize
 * @returns Normalized number (always finite)
 * 
 * @example
 * normalizeNumber(123) // 123
 * normalizeNumber("456") // 456
 * normalizeNumber("invalid") // 0
 * normalizeNumber(null) // 0
 */
export function normalizeNumber(value: unknown): number {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : 0;
  }

  if (typeof value === 'string') {
    const parsed = Number.parseFloat(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  return 0;
}

